import cx from "classnames";
import { Button as NewButton } from "components/DesignSystem/Button/Button";
import { Form as FormikForm, Formik, useFormikContext } from "formik";
import { useContext, useState, useRef, useEffect, useMemo } from "react";
import { flushSync } from "react-dom";
import { Card, Form } from "react-bootstrap";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";

//components
import EditForm from "./editForm";
import { FormControl, FormTooltip } from "./formControl";

//api
import { stateUpdate } from "../../apis/stateUpdate";
import { putForm } from "../../apis/taskForm";

//context
import classNames from "classnames";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { createAutofill } from "../../apis/autofill";
import authContext from "../../jwt_context&axios/authContext";
import {
  getAutofillFields,
  getKeysAndValuesFromFields,
} from "../../utils/formEngineHelpers";
import { getFieldsFromSections } from "util/getFieldsFromSections";
import TaskButtonBar from "./TaskButtonBar";
import { UpdateTaskPrice } from "./updateTaskPrice";
import { FillFormStepper } from "./fillFormStepper/FillFormStepper";
import { useDispatch, useSelector } from "react-redux";
import { openTaskReview, setTaskUpdatedSection } from "store/slices/TaskReview";
import { FilingReviewModal } from "./FilingReviewModal";
import { IRSSubmitModal } from "./IRSSubmitModal";
import { useParams } from "react-router-dom";
import { setIsFormSubmitClicked, setShowPricingModal } from "store/slices/task";
import { ADDRESS_AUTOFILL_VERSION_2_0 } from "constants/task";
import { FILING_DESCRIPTION_DIVIDER_CLASS } from "./FilingReview/FilingSectionReview";
import FilingInprogressIllustration from "static/images/FilingInprogressIllustration.svg";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { STATE_FRANCHISE_TAX_DELAWARE } from "types/Models/services";
import { BUSINESS_FINANCIALS_SECTION_KEY } from "constants/estimatedTaxSections";
import { getEstimatedTaxQuarter } from "util/getEstimatedTaxQuarter";
import { TaskNoteIcon } from "components/icons/TaskNoteIcon";

import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { FormSections } from "./FormSections";
import { PlusIcon } from "components/icons/PlusIcon";
import { TrashSimple } from "components/icons/TrashSimple";
import { useModal } from "hooks/useModal";
import {
  useAmendFilingMutation,
  useUpdateTaskFormDataMutation,
} from "store/apis/task";
import {
  FILING_IN_PROGRESS,
  TASK_AMENDMENT_COMPLETED,
} from "constants/taskStates";
import { UploadFile } from "components/UploadFile/UploadFile";
import { useGetIRSSubmissionStatusQuery } from "store/apis/IRSDetails";
import { IRSSubmissionStatus } from "./IRSSubmissionStatus";
import { RAndDNoteModal } from "./RAndDNoteModal";
import { filterServerDocuments } from "utils/filterServerDocuments";
import { TaskMarkdown } from "./TaskMarkdown";
import { SubSectionFields } from "./SubSectionFields";
import { FIELD_TYPE } from "types/Models/Filing";
import { getUploadOrLinkInitialValues } from "util/booksHandoff/getUploadOrLinkInitialValues";
import { getUploadOrLinkSavedField } from "util/booksHandoff/getUploadOrLinkSavedField";
import { hasSubSections } from "util/booksHandoff/hasSubSections";
import { isConditionMet } from "util/booksHandoff/isConditionMet";
import { scrollToElement } from "util/scrollToElement";
import { getActiveScrollSection } from "util/getActiveScrollSection";
import { getFieldChecks } from "util/filingForm/getFieldChecks";
import { getFieldKeysOnPage } from "util/filingForm/getFieldKeysOnPage";
import { getFormErrors } from "util/filingForm/getFormErrors";
import { FormPendingCountTracker } from "./FormPendingCountTracker";
import { getFirstErrorField } from "util/filingForm/getFirstErrorField";
import { FormPendingCount } from "./FormPendingCount";

dayjs.extend(customParseFormat);

const NO_FIELDS = [];
const NO_SECTIONS = [];
const EMPTY_PENDING_COUNT = { pending: 0, total: 0, sections: {} };
const FIRST_MOBILE_SECTION = "0";
const MOBILE_SECTION_SELECTOR = "[data-section-index]";

const Fields = ({
  fields,
  hide,
  hidden,
  current,
  data,
  uuid,
  repeatableId,
  groupId,
  updatedTask,
  formId,
  sectionKey,
  isArchived,
  formData,
  section,
  entityId,
  season,
  quarter,
  deadline,
  radioValues,
  setRadioValues,
  entityName,
}) => {
  const { authtoken } = useContext(authContext);
  const { isAdmin, isCustomer } = useRoleBasedView();
  let cpa_user = authtoken.is_service_user;
  const { setFieldValue, values } = useFormikContext();

  const onChange = (collapsable) => (event) => {
    if (collapsable) {
      hide?.(event.target.checked);
    }
  };

  const FPT_CHAIN = [
    "fpt_permitted_purpose",
    "fpt_technological_nature",
    "fpt_elimination_uncertainty",
    "fpt_experimentation_process",
  ];

  const onRadioChange = (fieldKey, value) => {
    setFieldValue(fieldKey, value);
    setRadioValues((prev) => {
      const next = { ...prev, [fieldKey]: value };
      // When do_you_have_us_payroll or filing_choice changes, clear filing_choice and all FPT answers
      if (fieldKey === "do_you_have_us_payroll") {
        next["filing_choice"] = "";
        setFieldValue("filing_choice", "");
        FPT_CHAIN.forEach((key) => {
          next[key] = "";
          setFieldValue(key, "");
        });
      }
      if (fieldKey === "filing_choice") {
        FPT_CHAIN.forEach((key) => {
          next[key] = "";
          setFieldValue(key, "");
        });
      }
      // When a FPT question changes, clear all downstream FPT answers
      const idx = FPT_CHAIN.indexOf(fieldKey);
      if (idx !== -1) {
        FPT_CHAIN.slice(idx + 1).forEach((key) => {
          next[key] = "";
          setFieldValue(key, "");
        });
      }
      return next;
    });
  };

  let selectedCurrency = "USD";
  fields?.forEach((field) => {
    if (field?.field_key?.includes("currency") && values[field.field_key]) {
      selectedCurrency = values[field.field_key]?.split(" - ")?.[0];
    }
  });

  if (!fields) {
    return null;
  }

  return fields?.map((field, index) => {
    const nextColumnField = fields[index + 1];
    const showDescriptionBefore =
      field?.show_description_before ||
      (nextColumnField?.show_description_before && field.is_first_column); // is_first_column is to check if it comes in multi column input otherwise it will come twice (first: for next column and then current column )
    const showDescriptionAfter =
      field?.show_description_after ||
      (nextColumnField?.show_description_after && field.is_first_column);

    let hideField =
      Object.entries(field?.radio || {}).length > 0
        ? !Object.keys(field.radio).some(
            (key) => radioValues[key] === field.radio[key]
          )
        : false;
    // books handoff fields can also follow select answers
    if (hasSubSections(section)) {
      hideField = !isConditionMet(field, radioValues, values);
    }

    const skipField =
      (field.is_hidden_for_customer && isCustomer) ||
      (field.is_hidden_for_admin && !isCustomer);

    if (field.is_second_column || hideField || skipField) {
      return <></>;
    }

    return (
      (!hidden || field.collapse_section) && (
        <>
          {showDescriptionBefore && (
            <span
              className={FILING_DESCRIPTION_DIVIDER_CLASS}
              dangerouslySetInnerHTML={{
                __html: showDescriptionBefore,
              }}
            />
          )}
          <Form.Group
            className={cx({
              "opacity-50": field.is_hidden_for_customer && isCustomer,
              "p-2":
                (!field.is_hidden_for_customer || isAdmin) &&
                !field.should_hide_section_field,
              "t-flex t-gap-4 sm:t-flex-col": field.is_first_column,
            })}
            key={field.field_key}
          >
            <div
              className={classNames("t-w-full", {
                "t-hidden": field.should_hide_section_field,
              })}
              data-field-key={
                field.should_hide_section_field ? undefined : field.field_key
              }
            >
              <FormControl
                authtoken={authtoken}
                sectionKey={sectionKey}
                formId={formId}
                updatedTask={updatedTask}
                disabled={
                  (!cpa_user && field.field_key === "grose_assets") ||
                  field.disabled
                }
                type={field.type}
                id={field.field_key}
                name={field.field_key}
                label={field.field_label}
                placeholder={field.placeholder}
                options={field.options}
                tooltip={field.tooltip}
                defaultValue={field.value}
                onChange={onChange(field.collapse_section)}
                descript={field?.description}
                mandatory={field?.required}
                current={current}
                data={data}
                uuid={uuid}
                groupId={groupId}
                autofillKey={field.autofill_key}
                autofillPrefix={field.autofill_prefix}
                autofillSuffix={repeatableId}
                tag={field.tag}
                document={field.document}
                field={field}
                isArchived={isArchived}
                formData={formData}
                section={section}
                entityId={entityId}
                fields={fields}
                onRadioChange={(value) => onRadioChange(field.field_key, value)}
                season={season}
                quarter={quarter}
                deadline={deadline}
                selectedCurrency={selectedCurrency}
                entityName={entityName}
              />
            </div>

            {field.is_first_column && nextColumnField?.is_second_column && (
              <div
                className="t-w-full"
                data-field-key={nextColumnField.field_key}
              >
                <FormControl
                  authtoken={authtoken}
                  sectionKey={sectionKey}
                  formId={formId}
                  updatedTask={updatedTask}
                  disabled={
                    (!cpa_user &&
                      nextColumnField.field_key === "grose_assets") ||
                    nextColumnField.disabled
                  }
                  type={nextColumnField.type}
                  id={nextColumnField.field_key}
                  name={nextColumnField.field_key}
                  label={nextColumnField.field_label}
                  placeholder={nextColumnField.placeholder}
                  options={nextColumnField.options}
                  tooltip={nextColumnField.tooltip}
                  defaultValue={nextColumnField.value}
                  onChange={onChange(nextColumnField.collapse_section)}
                  descript={nextColumnField?.description}
                  mandatory={nextColumnField?.required}
                  current={current}
                  data={data}
                  uuid={uuid}
                  groupId={groupId}
                  autofillKey={nextColumnField.autofill_key}
                  autofillPrefix={nextColumnField.autofill_prefix}
                  autofillSuffix={repeatableId}
                  tag={nextColumnField.tag}
                  document={nextColumnField.document}
                  field={nextColumnField}
                  isArchived={isArchived}
                  formData={formData}
                  section={section}
                  entityId={entityId}
                  fields={fields}
                  onRadioChange={(value) =>
                    onRadioChange(field.field_key, value)
                  }
                  deadline={deadline}
                  season={season}
                  quarter={quarter}
                  selectedCurrency={selectedCurrency}
                  entityName={entityName}
                />
              </div>
            )}
          </Form.Group>
          {showDescriptionAfter && (
            <span
              className={FILING_DESCRIPTION_DIVIDER_CLASS}
              dangerouslySetInnerHTML={{
                __html: showDescriptionAfter,
              }}
            />
          )}
        </>
      )
    );
  });
};

const RepeatableFields = ({
  repeatableFields,
  duplicationAllowed,
  title,
  hidden,
  updateSectionKeyIdPair,
  sectionKey,
  updateRemovedSectionIds,
  current,
  data,
  uuid,
  groupId,
  updatedTask,
  formId,
  isArchived,
  section,
  entityId,
  formData,
  season,
  quarter,
  deadline,
  radioValues,
  setRadioValues,
  entityName,
}) => {
  const [repeatables, setFields] = useState(repeatableFields);
  const addField = () => {
    const groupHeader = repeatables[0].group_header || section.section_title;
    const ids = repeatables.map(({ id }) => id);
    const maxId = Math.max(...ids);
    const nextId = maxId + 1;
    const templateFields = repeatables[0].fields.map((field) => {
      let keyOnly = field.field_key.split("_");
      keyOnly = keyOnly.slice(0, keyOnly.length - 1);
      keyOnly = keyOnly.join("_");
      let updatedField = field.hasOwnProperty("radio")
        ? {
            ...field,
            radio: Object.keys(field.radio).reduce((acc, key) => {
              let radioKey = key.split("_");
              radioKey[radioKey.length - 1] = nextId.toString();
              radioKey = radioKey.join("_");
              acc[radioKey] = field.radio[key];
              return acc;
            }, {}),
          }
        : field;
      return {
        ...updatedField,
        value: "",
        documents: [],
        field_key: `${keyOnly}_${nextId}`,
      };
    });

    setFields((r) => [
      ...r,
      {
        id: nextId,
        group_header: groupHeader,
        fields: templateFields,
        selected_autofill_details: {},
      },
    ]);
    updateSectionKeyIdPair({ key: sectionKey, id: nextId });
  };

  useEffect(() => {
    setFields(repeatableFields);
  }, [repeatableFields]);

  const updatedSection = { ...section, repeatable_fields: repeatables };

  if (hidden) {
    return null;
  }

  const onRemove = (removeId) => () => {
    setFields((r) => r.filter(({ id }) => id !== removeId));
    updateRemovedSectionIds({ key: sectionKey, id: removeId });
  };

  return (
    <>
      {repeatables?.map(({ fields, id, group_header }, index) => (
        <Card key={id} className="mb-4">
          <Card.Body>
            <div className="t-flex t-justify-between t-items-center t-px-2 t-mb-2">
              <div className="t-text-text-100 t-text-subtext">
                {group_header || section.section_title} {index + 1}
              </div>
              <NewButton
                customType="icon"
                size="small"
                onClick={onRemove(id)}
                disabled={repeatables.length === 1}
                type="button"
              >
                <span className="t-text-text-30">
                  <TrashSimple />
                </span>
              </NewButton>
            </div>
            <Fields
              formId={formId}
              updatedTask={updatedTask}
              fields={fields}
              repeatableId={id.toString()}
              current={current}
              data={data}
              uuid={uuid}
              groupId={groupId}
              sectionKey={sectionKey}
              isArchived={isArchived}
              formData={formData}
              section={updatedSection}
              entityId={entityId}
              season={season}
              quarter={quarter}
              deadline={deadline}
              radioValues={radioValues}
              setRadioValues={setRadioValues}
              entityName={entityName}
            />
          </Card.Body>
        </Card>
      ))}
      {duplicationAllowed && (
        <NewButton type="button" onClick={addField} block>
          <PlusIcon /> &nbsp; Add {title}
        </NewButton>
      )}
    </>
  );
};

export const AllFields = ({
  section,
  updateSectionKeyIdPair,
  updateRemovedSectionIds,
  current,
  data,
  uuid,
  groupId,
  updatedTask,
  formId,
  isArchived,
  entityId,
  formData,
  season,
  quarter,
  deadline,
  entityName,
  lightDescription,
}) => {
  const field = section?.section_fields?.[0];
  const [hidden, hide] = useState(
    field?.type === "checkbox" && field?.value === "true"
  );
  const { values } = useFormikContext();
  const [radioValues, setRadioValues] = useState(() => {
    let radioData =
      section?.section_fields &&
      section?.section_fields.reduce((acc, field) => {
        if (field.type === "radio") {
          acc[field.field_key] = field.value;
        }
        return acc;
      }, {});
    let radioData1 = {};
    section?.repeatable_fields?.forEach(({ fields }) => {
      fields?.forEach((field) => {
        if (field.type === "radio") {
          radioData1[field.field_key] = field.value;
        }
      });
    });

    return { ...radioData, ...radioData1 };
  });

  let hideRepeatableField = section.repeatable_fields?.[0]?.radio
    ? !Object.keys(section.repeatable_fields?.[0]?.radio).some(
        (key) => radioValues[key] === section.repeatable_fields[0]?.radio[key]
      )
    : false;

  const isSubSectioned = hasSubSections(section);
  const sectionFields = isSubSectioned ? NO_FIELDS : section?.section_fields;

  const renderSubSectionFields = (fields) => (
    <Fields
      sectionKey={section.section_key}
      formId={formId}
      updatedTask={updatedTask}
      fields={fields}
      removable={section?.is_removable}
      hide={hide}
      hidden={hidden}
      current={current}
      data={data}
      uuid={uuid}
      sectionId={section.uuid}
      groupId={groupId}
      isArchived={isArchived}
      formData={formData}
      section={section}
      entityId={entityId}
      season={season}
      quarter={quarter}
      deadline={deadline}
      radioValues={radioValues}
      setRadioValues={setRadioValues}
      entityName={entityName}
    />
  );

  return (
    <>
      <TaskMarkdown
        className={cx("t-text-body-sm t-ml-2 t-mt-1", {
          "t-text-text-30": lightDescription,
          "t-text-text-60": !lightDescription,
          "t-mb-2": section.section_key === BUSINESS_FINANCIALS_SECTION_KEY,
        })}
        text={section.section_description}
        taskContext={{
          taskId: uuid,
          groupId: groupId,
          entityId: entityId,
        }}
      />
      {isSubSectioned && (
        <SubSectionFields
          section={section}
          radioValues={radioValues}
          renderFields={renderSubSectionFields}
        />
      )}
      <Fields
        sectionKey={section.section_key}
        formId={formId}
        updatedTask={updatedTask}
        fields={sectionFields}
        removable={section?.is_removable}
        hide={hide}
        hidden={hidden}
        current={current}
        data={data}
        uuid={uuid}
        sectionId={section.uuid}
        groupId={groupId}
        isArchived={isArchived}
        formData={formData}
        section={section}
        entityId={entityId}
        season={season}
        quarter={quarter}
        deadline={deadline}
        radioValues={radioValues}
        setRadioValues={setRadioValues}
        entityName={entityName}
      />
      {section.should_hide_repeatable_fields || hideRepeatableField ? (
        <></>
      ) : (
        <RepeatableFields
          formId={formId}
          updatedTask={updatedTask}
          updateSectionKeyIdPair={updateSectionKeyIdPair}
          duplicationAllowed={section.section_duplication_allowed}
          sectionKey={section.section_key}
          repeatableFields={section.repeatable_fields}
          title={section?.section_title}
          hidden={hidden}
          updateRemovedSectionIds={updateRemovedSectionIds}
          current={current}
          data={data}
          uuid={uuid}
          groupId={groupId}
          isArchived={isArchived}
          section={section}
          entityId={entityId}
          formData={formData}
          season={season}
          quarter={quarter}
          deadline={deadline}
          radioValues={radioValues}
          setRadioValues={setRadioValues}
          entityName={entityName}
        />
      )}
    </>
  );
};
export const FormEngine = ({
  next,
  prev,
  inkleDocId,
  description,
  mainId,
  form_data,
  current,
  data,
  uuid,
  groupId,
  task,
  updatedTask,
  isArchived,
  docId,
  isFiling,
  parentRef,
  entityId,
  isEditableForAdmin,
  isSectionGroupAvailable,
  isAmendment,
  taskStateType,
  nextBtnText = "Next",
}) => {
  const { taskId } = useParams();
  const { isCustomer, isAdmin } = useRoleBasedView();
  const { authtoken } = useContext(authContext);
  const [showUpdatePriceModal, setShowUpdatePriceModal] = useState(false);
  const [cBtn, setCBtn] = useState(0);
  const [sectionKeyIdPair, setSectionKeyIdPair] = useState({});
  const [removedSectionIds, setRemovedSectionIds] = useState({});
  const [apiLoading, setApiLoading] = useState(false);
  const [isVariablePricing, setIsVariablePricing] = useState(false);
  const isMarkAsFiled = nextBtnText === "Mark as Filed";
  let newStateId = next;
  if (task?.state?.is_step_replaceable) newStateId = task?.state?.new_state_id;
  if (isMarkAsFiled)
    newStateId = task?.task_states?.find((s) => s.name === "Completed")?.uuid;
  const irsFormReviewModal = useModal();
  const rAndDNoteModal = useModal();
  const [updateTaskFormData] = useUpdateTaskFormDataMutation();
  const showInfoModalFor = form_data?.data?.data?.show_info_modal_for;
  const quarter = getEstimatedTaxQuarter(form_data);

  useEffect(() => {
    if (showInfoModalFor === "FORM_6765") {
      rAndDNoteModal.open();
    }
  }, [showInfoModalFor]);

  const closeRAndDNoteModal = async () => {
    try {
      rAndDNoteModal.close();
      await updateTaskFormData({
        formId: form_data.uuid,
        taskId,
        payload: {
          form_data: {
            ...form_data,
            data: {
              ...form_data.data.data,
              show_info_modal_for: null,
            },
          },
        },
      }).unwrap();
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
  };
  const { data: irsSubmissionData } = useGetIRSSubmissionStatusQuery(
    {
      taskId,
    },
    {
      skip: !isAdmin || !taskId,
    }
  );
  const formSections = form_data?.data?.data?.section_group?.[0]?.sections?.map(
    (section) => ({
      sectionTitle: section?.section_title,
      sectionKey: section?.section_key,
      is_hidden_for_customer: section?.is_hidden_for_customer,
      is_hidden_for_admin: section?.is_hidden_for_admin,
    })
  );
  const visibleFormSections =
    formSections?.filter(
      ({ is_hidden_for_customer, is_hidden_for_admin }) =>
        !(is_hidden_for_customer && isCustomer) &&
        !(is_hidden_for_admin && isAdmin)
    ) ?? NO_SECTIONS;
  const [formPendingCount, setFormPendingCount] = useState(EMPTY_PENDING_COUNT);
  const [activeSectionKey, setActiveSectionKey] = useState(
    visibleFormSections[0]?.sectionKey
  );
  // Section picked in the left nav; it stays highlighted until the user scrolls by hand
  const navTargetSectionRef = useRef(null);
  const { showTaskReviewStep, showTaskReview } = useSelector(
    (state) => state.taskReview
  );
  const isPricingStep = useSelector((state) => state.taskSlice.isPricingStep);
  const dispatch = useDispatch();
  const [form, setForm] = useState();
  const [formValues, setFormValues] = useState();
  const { alertToast } = useToast();
  const { width } = useWindowSize();
  const isSmallScreen = width <= 640;
  const [openMobileSection, setOpenMobileSection] =
    useState(FIRST_MOBILE_SECTION);
  const formRef = useRef(null);
  // completion only checks (books handoff) apply to Next, not Prev or keyboard submits
  const isCompletingActionRef = useRef(false);
  const uploadFileModal = useModal();
  const [amendTask, { isLoading }] = useAmendFilingMutation();
  const isFilingInProgress = taskStateType === FILING_IN_PROGRESS;
  const is7004Filing =
    task.base_task_key === process.env.PUBLIC_FORM_7004_FEDERAL_TAX_EXTENSION;
  const showSubmitIRSTrigger =
    !irsSubmissionData?.status && isFilingInProgress && isAdmin && is7004Filing;
  const isSubmittingToIRS = irsSubmissionData?.status && isAdmin;

  const onFileSelect = async (attachments) => {
    try {
      const inkleDocs = [];
      const localFiles = [];
      attachments.forEach((attachment) => {
        if ("uuid" in attachment) {
          inkleDocs.push(attachment.uuid);
        } else {
          //@ts-ignore
          localFiles.push(attachment);
        }
      });
      const groupId = task?.group_uuid;
      const taskId = task?.uuid;
      const formData = new FormData();
      formData.append("file_ids", inkleDocs.join(","));
      localFiles.forEach((file) => formData.append("files", file));
      formData.append("next_state_type", TASK_AMENDMENT_COMPLETED);

      await amendTask({
        taskId,
        groupId,
        payload: formData,
      }).unwrap();
      uploadFileModal.close();
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const setActiveSection = (activeSection) => {
    navTargetSectionRef.current = activeSection;
    scrollToElement(activeSection);
    setActiveSectionKey(activeSection);
  };

  const releaseNavTargetSection = () => {
    navTargetSectionRef.current = null;
  };

  // errors must already be rendered: sub-section cards holding one open on render
  const scrollToFirstError = (validationErrors) => {
    const firstErrorField = getFirstErrorField(
      formRef.current,
      validationErrors
    );

    if (!firstErrorField) {
      return;
    }

    const mobileSection = firstErrorField.closest(MOBILE_SECTION_SELECTOR);

    if (mobileSection) {
      flushSync(() => setOpenMobileSection(mobileSection.dataset.sectionIndex));
    }

    releaseNavTargetSection();
    scrollToElement(firstErrorField);
  };

  const handleSectionsScroll = (e) => {
    if (navTargetSectionRef.current) {
      return;
    }

    const sectionElements = visibleFormSections
      .map(({ sectionKey }) => document.getElementById(sectionKey))
      .filter(Boolean);
    const activeSection = getActiveScrollSection(
      e.currentTarget,
      sectionElements
    );

    if (activeSection) {
      setActiveSectionKey(activeSection.id);
    }
  };

  const updateSectionKeyIdPair = ({ key, id }) =>
    setSectionKeyIdPair((keyIdPairs) => ({ ...keyIdPairs, [key]: id }));

  const updateRemovedSectionIds = ({ key, id }) =>
    setRemovedSectionIds((keyIdPairs) => ({
      ...keyIdPairs,
      [key]: [...(keyIdPairs[key] || []), id],
    }));

  const normalizeDate = (value) => {
    if (dayjs(value, YYYY_MM_DD, true).isValid()) {
      return value;
    }

    if (dayjs(value, DISPLAY_DATE, true).isValid()) {
      return dayjs(value, DISPLAY_DATE).format(YYYY_MM_DD);
    }

    return null;
  };

  const getUpdatedSections = (formValues, values) => {
    const formData = new FormData(formValues);
    let formNameValue = { ...values };
    for (var pair of formData.entries()) {
      formNameValue[pair[0]] = pair[1];
    }
    const updatedValue = (key, type) => {
      if (type === "multiselect") {
        return values[key];
      }

      if (type === "date") {
        return formNameValue[key] ? normalizeDate(formNameValue[key]) : "";
      }

      return formNameValue[key];
    };

    const updatedSections =
      form_data?.data?.data?.section_group?.[0]?.sections?.map((section) => {
        const updatedSectionFields = section?.section_fields?.map((field) => {
          if (field.type === "checkbox") {
            const currentCheckboxValue = updatedValue(
              field.field_key,
              field.type
            );
            return {
              ...field,
              value:
                currentCheckboxValue === "" || currentCheckboxValue === "true"
                  ? "true"
                  : null,
            };
          }
          if (field.type === FIELD_TYPE.UPLOAD_OR_LINK) {
            const savedField = getUploadOrLinkSavedField(field, values);
            return {
              ...savedField,
              documents: filterServerDocuments(savedField.documents),
            };
          }
          if (field.type === "file_upload") {
            const documents = values[field.field_key] ?? field.documents;

            return {
              ...field,
              documents: filterServerDocuments(documents),
            };
          }
          return {
            ...field,
            value: updatedValue(field.field_key, field.type),
          };
        });

        let updatedSection = {
          ...section,
          section_fields: updatedSectionFields,
        };

        if (section.repeatable_fields?.length > 0) {
          if (
            section.section_key === "directors" &&
            section.should_hide_repeatable_fields
          ) {
            return {
              ...updatedSection,
              repeatable_fields:
                form_data?.data?.data?.section_group?.[0]?.sections?.find(
                  (section) => section.section_key === "directors"
                )?.repeatable_fields,
            };
          }

          if (
            section.section_key === "shareholders" &&
            form_data?.data?.data.address_autofill_version ===
              ADDRESS_AUTOFILL_VERSION_2_0
          ) {
            return {
              ...section,
              repeatable_fields:
                form_data?.data?.data?.section_group?.[0]?.sections?.find(
                  (section) => section.section_key === "shareholders"
                )?.repeatable_fields,
            };
          }
          const groupHeader =
            updatedSection?.repeatable_fields[0]?.group_header;
          updatedSection.repeatable_fields = updatedSection?.repeatable_fields
            ?.filter(
              ({ id }) =>
                !removedSectionIds[updatedSection.section_key]?.includes(id)
            )
            .map((repeatable) => ({
              ...repeatable,
              id: repeatable.id,
              fields: repeatable.fields?.map((field) => ({
                ...field,
                value:
                  updatedValue(field?.field_key, field?.type) || field.value,
              })),
              group_header: groupHeader || null,
            }));

          const finalId = sectionKeyIdPair[section.section_key];
          const allId = updatedSection?.repeatable_fields?.map((f) => f.id);
          const existingMaxId = Math.max(...allId);

          if (finalId && finalId - existingMaxId > 0) {
            const noOfFieldsToCreate = finalId - existingMaxId;

            const separateFieldKey = (fieldWithId) => {
              let nameOnly = fieldWithId.split("_");
              nameOnly = nameOnly.slice(0, nameOnly.length - 1);
              return nameOnly.join("_");
            };

            for (let i = 1; i <= noOfFieldsToCreate; i++) {
              const fields = updatedSection.repeatable_fields[0].fields.map(
                (field) => {
                  let keyOnly = separateFieldKey(field.field_key);
                  let autofillSection = field.autofill_section
                    ? separateFieldKey(field.autofill_section)
                    : null;
                  const fieldKey = `${keyOnly}_${existingMaxId + i}`;
                  let updatedField = field.hasOwnProperty("radio")
                    ? {
                        ...field,
                        radio: Object.keys(field.radio).reduce((acc, key) => {
                          let radioKey = key.split("_");
                          radioKey[radioKey.length - 1] = (
                            existingMaxId + i
                          ).toString();
                          radioKey = radioKey.join("_");
                          acc[radioKey] = field.radio[key];
                          return acc;
                        }, {}),
                      }
                    : field;
                  const returnObj = {
                    ...updatedField,
                    value: updatedValue(fieldKey, field?.type),
                    field_key: fieldKey,
                  };

                  if (autofillSection) {
                    returnObj.autofill_section = `${autofillSection}_${
                      existingMaxId + i
                    }`;
                  }

                  return returnObj;
                }
              );

              updatedSection.repeatable_fields.push({
                id: existingMaxId + i,
                fields,
              });
            }
          }
        }
        return updatedSection;
      });

    return updatedSections;
  };

  const openTaskReviewModal = (e, values) => {
    e?.preventDefault();
    const form = e.target;
    setForm(form);
    setFormValues(values);
    const updatedSection = getUpdatedSections(form, values);
    if (cBtn === 2) {
      handleFormSubmit(form, values);
    } else {
      dispatch(setTaskUpdatedSection(updatedSection));
      dispatch(openTaskReview());
    }
  };

  const onPreSave = async () => {
    const params = await buildFormPayload(form, formValues);
    return putForm({ formId: form_data.uuid, params });
  };

  const handleSubmit = (e, values) => {
    e?.preventDefault();
    handleFormSubmit(e.target, values);
  };

  const handleSubmitToIRS = async (formValues, values, irsValues) => {
    try {
      const params = await buildFormPayload(formValues, values);
    } catch (e) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const buildFormPayload = async (formValues, values) => {
    const updatedSections = getUpdatedSections(formValues, values);

    if (
      form_data.data.data.address_autofill_version !==
      ADDRESS_AUTOFILL_VERSION_2_0
    ) {
      const [keyAndSectionMap, sectionToAutofillFieldsMap] =
        getAutofillFields(updatedSections);

      Object.entries(sectionToAutofillFieldsMap).map(
        async ([section, fields]) => {
          await createAutofill({
            entityId,
            groupId,
            fields: getKeysAndValuesFromFields(fields),
            autofillKey: keyAndSectionMap[section],
          });
        }
      );
    }

    return {
      form_data: {
        ...form_data,
        data: {
          ...form_data.data.data,
          section_group: [{ sections: updatedSections }],
        },
      },
    };
  };

  const handleFormSubmit = async (formValues, values, actionBtn) => {
    setCBtn(2);
    const params = await buildFormPayload(formValues, values);

    putForm({
      formId: form_data.uuid,
      params,
    }).then((response) => {
      setApiLoading(false);
      let id;

      if (isAmendment) {
        uploadFileModal.open();
        return;
      }

      // cBtn = 1 -> save and stateChange to next
      // cBtn = 2 -> save and stay on same screen
      // cBtn = 3 -> save and stateChange to prev
      if (cBtn === 1 || actionBtn === 1) {
        id = next;
        if (!isCustomer && task.is_next_state_final) {
          setShowUpdatePriceModal(true);
          if (task.is_pricing_fixed) {
            setIsVariablePricing(false);
          } else {
            setIsVariablePricing(true);
          }
        } else {
          stateUpdate({
            taskId,
            id: task?.state?.is_step_replaceable
              ? task?.state?.new_state_id
              : id,
          }).then(() => {
            window.location.reload();
          });
        }
      } else if (cBtn === 2 || actionBtn === 2) {
        window.location.reload();
      } else if (cBtn === 3 || actionBtn === 3) {
        id = prev;
        stateUpdate({
          taskId,
          id,
        }).then(() => {
          window.location.reload();
        });
      }
    });
  };

  const allSections =
    form_data?.data?.data?.section_group?.flatMap(
      (group) => group.sections ?? NO_SECTIONS
    ) ?? NO_SECTIONS;

  const validateFilingForm = (values) =>
    getFormErrors(
      getFieldChecks({
        sections: allSections,
        values,
        fieldKeysOnPage: getFieldKeysOnPage(formRef.current),
        isCompletingTask:
          Boolean(task?.is_next_state_final) && isCompletingActionRef.current,
        addedRepeatableIds: sectionKeyIdPair,
      })
    );

  const handlePrevClick = () => {
    isCompletingActionRef.current = false;
    setCBtn(3);
  };

  const handleNextClick = () => {
    isCompletingActionRef.current = true;
    setCBtn(1);
  };

  if (!form_data) {
    return null;
  }

  const formInitialValues =
    form_data?.data?.data?.section_group?.[0]?.sections?.reduce(
      (currentValue, section) => {
        const addFieldValue = (field) => {
          if (field.type === "checkbox") {
            currentValue[field.field_key] = Boolean(field.value);
          } else if (field.type === "file_upload") {
            currentValue[field.field_key] = field.documents;
          } else if (field.type === FIELD_TYPE.UPLOAD_OR_LINK) {
            Object.assign(currentValue, getUploadOrLinkInitialValues(field));
          } else {
            currentValue[field.field_key] = field.value;
          }
        };

        section.section_fields?.forEach((field) => {
          addFieldValue(field);
        });

        section.repeatable_fields?.forEach(({ fields }) =>
          fields?.forEach((field) => {
            addFieldValue(field);
          })
        );

        return currentValue;
      },
      {}
    );

  const allFields = getFieldsFromSections(
    form_data?.data?.data?.section_group?.[0]?.sections
  );

  return (
    <div className="t-h-full t-w-full t-pt-4">
      {!isArchived && (
        <EditForm current={current} data={data} uuid={uuid} isFromStep0 />
      )}
      <Formik
        initialValues={formInitialValues}
        className="t-h-full"
        enableReinitialize
        validate={validateFilingForm}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
      >
        {({ values, validateForm, setTouched }) => {
          const isNextDisabled = allFields.some(
            (field) =>
              field?.disable_next_on !== undefined &&
              field?.disable_next_on !== null &&
              values[field.field_key] === field.disable_next_on
          );

          return (
            <FormikForm
              noValidate
              ref={formRef}
              onSubmit={(e) => {
                e?.preventDefault();
                dispatch(setIsFormSubmitClicked(true));
                validateForm().then((validationErrors) => {
                  if (Object.keys(validationErrors).length === 0) {
                    showTaskReviewStep
                      ? openTaskReviewModal(e, values)
                      : handleSubmit(e, values);
                  } else {
                    let touch = {};
                    Object.keys(validationErrors)?.forEach((error) => {
                      touch[error] = true;
                    });
                    flushSync(() => setTouched(touch));
                    scrollToFirstError(validationErrors);
                    alertToast({
                      title: "Validation Error",
                      message: "Please fill all the required fields",
                    });
                  }
                });
              }}
              className="taxing t-overflow-y-hidden t-w-full"
            >
              <FormPendingCountTracker
                formRef={formRef}
                sections={allSections}
                isCompletingTask={Boolean(task?.is_next_state_final)}
                addedRepeatableIds={sectionKeyIdPair}
                onChange={setFormPendingCount}
              />
              <FormPendingCount
                pending={formPendingCount.pending}
                total={formPendingCount.total}
              />
              {isSectionGroupAvailable ? (
                <div
                  className={cx("t-h-full t-w-full t-px-4", {
                    disabledForm:
                      (!mainId && !isEditableForAdmin) ||
                      isArchived ||
                      (isPricingStep && !isAdmin),
                  })}
                >
                  {isSmallScreen ? (
                    <Accordion.Root
                      type="single"
                      collapsible
                      value={openMobileSection}
                      onValueChange={setOpenMobileSection}
                      className="t-flex t-w-full t-flex-col t-gap-3 t-pb-36"
                    >
                      {form_data?.data?.data?.section_group?.[0]?.sections?.map(
                        (section, index) => {
                          return (
                            <Accordion.Item
                              value={String(index)}
                              data-section-index={String(index)}
                              key={section?.section_title}
                            >
                              <Accordion.Trigger>
                                {section?.section_title}
                              </Accordion.Trigger>
                              <Accordion.Content
                                forceMount
                                className={classNames(
                                  "t-px-2 t-pt-2 data-state-closed:t-hidden",
                                  {
                                    "t-pointer-events-none": isPricingStep,
                                  }
                                )}
                              >
                                <AllFields
                                  updatedTask={updatedTask}
                                  formId={form_data.uuid}
                                  section={section}
                                  updateSectionKeyIdPair={
                                    updateSectionKeyIdPair
                                  }
                                  updateRemovedSectionIds={
                                    updateRemovedSectionIds
                                  }
                                  current={current}
                                  data={data}
                                  uuid={uuid}
                                  groupId={groupId}
                                  isArchived={isArchived}
                                  entityId={entityId}
                                  formData={form_data}
                                  season={task.season}
                                  quarter={quarter}
                                  deadline={task.deadline}
                                  entityName={task?.entity?.name}
                                />
                              </Accordion.Content>
                            </Accordion.Item>
                          );
                        }
                      )}
                    </Accordion.Root>
                  ) : (
                    <div className="t-w-full t-h-full t-overflow-hidden t-flex t-mt-2 t-relative">
                      <div className="t-w-56 t-shrink-0 t-sticky t-mt-3.5">
                        <FillFormStepper>
                          {visibleFormSections.map(
                            ({ sectionKey, sectionTitle }) => (
                              <FillFormStepper.Step
                                key={sectionKey}
                                isActive={activeSectionKey === sectionKey}
                                sectionKey={sectionKey}
                                setActiveSectionKey={setActiveSection}
                                pendingCount={
                                  formPendingCount.sections[sectionKey]
                                }
                              >
                                {sectionTitle}
                              </FillFormStepper.Step>
                            )
                          )}
                        </FillFormStepper>
                      </div>
                      <div
                        className="t-flex-1 t-min-w-0 t-overflow-y-scroll t-mt-3.5 t-px-2 show-scrollbar"
                        onScroll={handleSectionsScroll}
                        onWheel={releaseNavTargetSection}
                        onTouchStart={releaseNavTargetSection}
                        onPointerDown={releaseNavTargetSection}
                        onKeyDown={releaseNavTargetSection}
                      >
                        <div>
                          {form_data?.data?.data?.task_custom_header && (
                            <div
                              className="t-pb-2 t-pl-6 t-border t-border-b-0 t-border-r-0 t-border-t-0 t-border-solid t-border-l-neutral-10"
                              dangerouslySetInnerHTML={{
                                __html: form_data.data.data.task_custom_header,
                              }}
                            />
                          )}
                          {form_data?.data?.data?.task_description && (
                            <div className="t-border t-border-b-0 t-border-r-0 t-border-t-0 t-border-solid t-border-l-neutral-10 t-pb-3 t-pl-6 t-text-body-sm">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: form_data.data.data.task_description,
                                }}
                              />
                            </div>
                          )}
                          {form_data?.data?.data?.task_note && (
                            <div className="t-border t-border-b-0 t-border-r-0 t-border-t-0 t-border-solid t-border-l-neutral-10 t-pb-6 t-pl-6 t-text-body-sm">
                              <div className="t-text-text-30 t-p-3 t-rounded-lg t-border t-border-solid t-border-blue-10 t-bg-blue-0 t-flex t-gap-2">
                                <span className="t-text-blue-70">
                                  <TaskNoteIcon />
                                </span>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: form_data.data.data.task_note,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="t-w-full sm:t-max-h-[500px] t-sticky">
                          {form_data?.data?.data?.section_group?.[0]?.sections
                            ?.filter(
                              (section) =>
                                !(
                                  section.is_hidden_for_customer && isCustomer
                                ) && !(section.is_hidden_for_admin && isAdmin)
                            )
                            ?.map((section) => (
                              <FormSections
                                key={section.section_key}
                                section={section}
                                isPricingStep={isPricingStep}
                                updatedTask={updatedTask}
                                formId={form_data.uuid}
                                updateSectionKeyIdPair={updateSectionKeyIdPair}
                                updateRemovedSectionIds={
                                  updateRemovedSectionIds
                                }
                                current={current}
                                data={data}
                                uuid={uuid}
                                groupId={groupId}
                                isArchived={isArchived}
                                entityId={entityId}
                                formData={form_data}
                                task={task}
                                quarter={quarter}
                                activeSectionKey={activeSectionKey}
                                lightDescription={
                                  section.section_key ===
                                  BUSINESS_FINANCIALS_SECTION_KEY
                                }
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="t-h-full t-w-full t-flex t-justify-center t-items-center">
                  <img
                    src={FilingInprogressIllustration}
                    alt="FilingInprogressIllustration"
                  />
                </div>
              )}

              {!isArchived && mainId && !apiLoading && !isPricingStep && (
                <TaskButtonBar
                  justifyContent="end"
                  parentRef={parentRef}
                  addMarginLeft={false}
                >
                  <div
                    className={classNames(
                      "t-flex t-gap-4 t-w-full t-px-5 sm:t-pl-4 sm:t-pr-0",
                      {
                        "t-justify-between": prev && authtoken.is_service_user,
                        "t-justify-end": !(prev && authtoken.is_service_user),
                      }
                    )}
                  >
                    {prev && authtoken.is_service_user && (
                      <NewButton
                        type="submit"
                        onClick={handlePrevClick}
                        customType="primary_outlined"
                      >
                        Prev
                      </NewButton>
                    )}

                    <div className="t-flex t-gap-2 sm:t-flex-1">
                      {showSubmitIRSTrigger && (
                        <NewButton
                          type="button"
                          onClick={irsFormReviewModal.open}
                          customType="primary_outlined"
                        >
                          Submit to IRS
                        </NewButton>
                      )}
                      {isSubmittingToIRS && (
                        <IRSSubmissionStatus
                          status={irsSubmissionData.status}
                        />
                      )}
                      <NewButton
                        type="button"
                        onClick={() =>
                          handleFormSubmit(formRef?.current, values, 2)
                        }
                        customType="primary_outlined"
                      >
                        Save
                      </NewButton>
                      <NewButton
                        type={
                          nextBtnText === "Mark as Filed" ? "button" : "submit"
                        }
                        onClick={
                          nextBtnText === "Mark as Filed"
                            ? () => setShowUpdatePriceModal(true)
                            : handleNextClick
                        }
                        customType="primary"
                        disabled={
                          nextBtnText !== "Mark as Filed" && isNextDisabled
                        }
                        className="sm:t-flex-1"
                      >
                        {nextBtnText}
                      </NewButton>
                    </div>
                  </div>
                </TaskButtonBar>
              )}
              {isPricingStep && (
                <TaskButtonBar
                  justifyContent="end"
                  parentRef={parentRef}
                  addMarginLeft={false}
                >
                  <div
                    className={classNames("t-flex t-gap-4", {
                      "t-pointer-events-auto t-justify-between": isAdmin,
                    })}
                  >
                    {isAdmin && (
                      <NewButton
                        type="button"
                        onClick={() =>
                          handleFormSubmit(formRef?.current, values, 2)
                        }
                        customType="primary_outlined"
                      >
                        Save
                      </NewButton>
                    )}
                    <NewButton
                      type="button"
                      onClick={() => dispatch(setShowPricingModal(true))}
                      customType="primary"
                    >
                      Pay Now
                    </NewButton>
                  </div>
                </TaskButtonBar>
              )}
            </FormikForm>
          );
        }}
      </Formik>

      <UpdateTaskPrice
        show={showUpdatePriceModal}
        setShow={setShowUpdatePriceModal}
        closeModal={() => setShowUpdatePriceModal(false)}
        groupId={groupId}
        newStateId={newStateId}
        taskId={taskId}
        task={task}
        isVariablePricing={isVariablePricing}
        entityId={entityId}
      />
      <IRSSubmitModal
        open={irsFormReviewModal.isOpen}
        onClose={irsFormReviewModal.close}
        taskId={taskId}
      />
      {showTaskReview && (
        <FilingReviewModal
          isArchived={isArchived}
          handleSubmit={() => handleFormSubmit(form, formValues)}
          onPreSave={onPreSave}
          task={task}
        />
      )}
      {uploadFileModal.isOpen && (
        <UploadFile
          title="Upload amendment document"
          showModal={uploadFileModal.isOpen}
          closeModal={uploadFileModal.close}
          onFileSelect={onFileSelect}
          prevSelectedFiles={[]}
          isLoading={isLoading}
        />
      )}
      <RAndDNoteModal
        isOpen={rAndDNoteModal.isOpen}
        close={closeRAndDNoteModal}
      />
    </div>
  );
};
